import type { MouseEvent, ReactNode } from 'react'
import { ChevronRight } from 'lucide-react'
import { formatDate, formatVisitDateTime, formatMoney, formatPercent, humanize, initials, isOverdue } from '../lib/formatters'
import type { AppRecord, ListColumn } from '../types'
import { PriorityBadge, StatusBadge } from './StatusBadge'

function renderValue(record: AppRecord, column: ListColumn) {
  const value = record[column.key] ?? (column.fallbackKey ? record[column.fallbackKey] : null)
  if (column.kind === 'status') return <StatusBadge value={value} />
  if (column.kind === 'priority') return <PriorityBadge value={value} />
  if (column.kind === 'datetime') return <span className="text-slate-600">{formatVisitDateTime(value)}</span>
  if (column.kind === 'date') {
    const overdue = column.key.toLowerCase().includes('due') && isOverdue(value) && record.status !== 'done'
    return <span className={overdue ? 'font-semibold text-rose-600' : 'text-slate-600'}>{formatDate(value)}</span>
  }
  if (column.kind === 'money') return <span className="font-semibold tabular-nums text-slate-800">{formatMoney(value)}</span>
  if (column.kind === 'percent') return <span className="font-semibold tabular-nums text-slate-800">{formatPercent(value)}</span>
  if (column.kind === 'boolean') return <StatusBadge value={value ? 'active' : 'inactive'} />
  if (column.kind === 'code') return <span className="font-mono text-xs font-semibold text-slate-700">{String(value || '—')}</span>
  if (column.kind === 'text') return <span className="line-clamp-1 text-slate-600">{String(value || '—')}</span>
  if (column.kind === 'primary') {
    const secondary = record.email || record.siteAddress || record.description || record.projectNumber || record.quoteNumber
    return (
      <div className="flex min-w-[190px] items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-pine-50 text-[11px] font-bold text-pine-700 ring-1 ring-pine-100">{initials(value)}</div>
        <div className="min-w-0">
          <div className="truncate font-semibold text-slate-900">{String(value || 'Untitled')}</div>
          {Boolean(secondary) && secondary !== value && <div className="mt-0.5 max-w-[260px] truncate text-xs text-slate-400">{String(secondary)}</div>}
        </div>
      </div>
    )
  }
  if (Array.isArray(value)) {
    const label = value.map((item) => typeof item === 'object' && item && 'name' in item ? String(item.name) : String(item)).join(', ')
    return <span className="line-clamp-1 text-slate-600">{label || '—'}</span>
  }
  return <span className="line-clamp-1 text-slate-600">{humanize(value)}</span>
}

export function DataTable({ records, columns, onRowClick, renderActions, minWidth }: {
  records: AppRecord[]
  columns: ListColumn[]
  onRowClick?: (record: AppRecord) => void
  renderActions?: (record: AppRecord) => ReactNode
  minWidth?: number
}) {
  const open = (record: AppRecord) => { if (onRowClick) onRowClick(record) }
  const actionClick = (event: MouseEvent) => event.stopPropagation()
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[760px] border-collapse text-left text-sm" style={minWidth ? { minWidth } : undefined}>
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50/70">
            {columns.map((column) => <th key={column.key} className="whitespace-nowrap px-5 py-3 text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500 first:pl-6">{column.label}</th>)}
            {(renderActions || onRowClick) && <th className="w-16 px-5 py-3"><span className="sr-only">Actions</span></th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {records.map((record) => (
            <tr
              key={record.id}
              onClick={() => open(record)}
              onKeyDown={(event) => { if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ') && onRowClick) { event.preventDefault(); open(record) } }}
              tabIndex={onRowClick ? 0 : undefined}
              className={onRowClick ? 'cursor-pointer bg-white transition-colors hover:bg-pine-50/40 focus:bg-pine-50/40 focus:outline-none' : 'bg-white'}
            >
              {columns.map((column) => <td key={column.key} className="px-5 py-3.5 first:pl-6">{renderValue(record, column)}</td>)}
              {(renderActions || onRowClick) && (
                <td className="px-4 py-3 text-right" onClick={actionClick}>
                  {renderActions?.(record) ?? <button type="button" aria-label={`Open ${String(record.name || record.quoteCode || record.id)}`} className="rounded p-2 text-slate-500 hover:bg-pine-50 hover:text-pine-700" onClick={() => open(record)}><ChevronRight className="h-4 w-4" /></button>}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
